import React, {
    createContext,
    useContext,
    useMemo,
    useCallback,
    ReactNode,
} from 'react';

import {
    useQuestions,
    useCreateQuestion,
    useUpdateQuestion,
    useDeleteQuestion,
} from '../hooks/useQuestions';

// =========================
// TYPES
// =========================

interface QuestionSource {
    sourceId: number;
    title: string;
    type: string;
}

interface Question {
    questionId: string;
    text: string;
    questionType: string;
    mcOptions: any[] | null;
    correctAnswers: string[];
    hints: string[];
    explanation: string;
    difficulty: number | string;
    chapter: string;
    feedback: string;
    isNew: boolean;
    needsReview: boolean;
    timesAnswered: number;
    timesCorrect: number;
    isActive: boolean;
    authorUserId: string | null;
    lastEditedByUserId: string | null;
    version: number;
    sourceId: number;
    source?: QuestionSource;
}

interface DifficultyStats {
    easy: number;
    medium: number;
    hard: number;
    veryHard: number;
    impossible: number;
    total: number;
}

interface SourceStat {
    sourceId: number;
    title: string;
    type: string;
    total: number;
    difficulties: {
        easy: number;
        medium: number;
        hard: number;
        veryHard: number;
        impossible: number;
    };
}

interface QuestionsContextValue {
    questions: Question[];
    sources: { id: number; title: string; type: string }[];
    activeQuestions: Question[];

    difficultyMap: Record<number, string>;
    difficulties: { id: number; label: string }[];

    questionTypeMap: Record<number, string>;
    questionTypes: { id: number; label: string }[];

    difficultyStats: DifficultyStats;
    sourceStats: SourceStat[];
    totalQuestions: number;

    isLoading: boolean;
    isError: boolean;
    error: unknown;

    addQuestion: (q: any) => Promise<void>;
    updateQuestion: (q: any) => Promise<void>;
    deleteQuestion: (id: any) => Promise<void>;

    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;
}

// Create Context
const QuestionsContext = createContext<QuestionsContextValue | null>(null);

// Provider
export const QuestionsProvider = ({ children }: { children: ReactNode }) => {
    // Fetch all questions
    const {
        data: questions = [],
        isLoading,
        isError,
        error,
    } = useQuestions();

    // Mutations
    const createMutation = useCreateQuestion();
    const updateMutation = useUpdateQuestion();
    const deleteMutation = useDeleteQuestion();

    // =========================
    // STATIC MAPS
    // =========================

    const difficultyMap = useMemo(
        () => ({
            1: 'Easy',
            2: 'Medium',
            3: 'Hard',
            4: 'Very Hard',
            5: 'Impossible',
        }),
        []
    );

    const difficulties = useMemo(
        () =>
            Object.entries(difficultyMap).map(([key, value]) => ({
                id: Number(key),
                label: value,
            })),
        [difficultyMap]
    );

    const questionTypeMap = useMemo(
        () => ({
            1: 'TextInput',
            2: 'MultipleChoice',
        }),
        []
    );

    const questionTypes = useMemo(
        () =>
            Object.entries(questionTypeMap).map(([key, value]) => ({
                id: Number(key),
                label: value,
            })),
        [questionTypeMap]
    );

    // =========================
    // DERIVED DATA
    // =========================

    const sources = useMemo(() => {
        const sourcesList = new Map<number, { title: string; type: string }>();

        (questions as Question[]).forEach((q) => {
            if (q.source) {
                sourcesList.set(q.source.sourceId, {
                    title: q.source.title,
                    type: q.source.type,
                });
            }
        });

        return Array.from(sourcesList, ([id, value]) => ({
            id,
            title: value.title,
            type: value.type,
        })).sort((a, b) => a.title.localeCompare(b.title));
    }, [questions]);

    const activeQuestions = useMemo(
        () => (questions as Question[]).filter((q) => q.isActive),
        [questions]
    );

    const totalQuestions = useMemo(
        () => questions.length,
        [questions]
    );

    // =========================
    // BREAKDOWN STATS
    // =========================

    const difficultyStats = useMemo((): DifficultyStats => {
        const stats: DifficultyStats = {
            easy: 0,
            medium: 0,
            hard: 0,
            veryHard: 0,
            impossible: 0,
            total: questions.length,
        };

        (questions as Question[]).forEach((q) => {
            switch (q.difficulty) {
                case 'Easy':
                case '1':
                case 1:
                    stats.easy++;
                    break;
                case 'Medium':
                case '2':
                case 2:
                    stats.medium++;
                    break;
                case 'Hard':
                case '3':
                case 3:
                    stats.hard++;
                    break;
                case 'Very Hard':
                case 'VeryHard':
                case '4':
                case 4:
                    stats.veryHard++;
                    break;
                case 'Impossible':
                case '5':
                case 5:
                    stats.impossible++;
                    break;
                default:
                    break;
            }
        });

        return stats;
    }, [questions]);

    const sourceStats = useMemo((): SourceStat[] => {
        const map = new Map<number, SourceStat>();

        (questions as Question[]).forEach((q) => {
            if (!q.source) return;

            const id = q.source.sourceId;

            if (!map.has(id)) {
                map.set(id, {
                    sourceId: id,
                    title: q.source.title,
                    type: q.source.type,
                    total: 0,
                    difficulties: {
                        easy: 0,
                        medium: 0,
                        hard: 0,
                        veryHard: 0,
                        impossible: 0,
                    },
                });
            }

            const entry = map.get(id)!;

            entry.total++;

            switch (q.difficulty) {
                case 'Easy':
                case '1':
                case 1:
                    entry.difficulties.easy++;
                    break;
                case 'Medium':
                case '2':
                case 2:
                    entry.difficulties.medium++;
                    break;
                case 'Hard':
                case '3':
                case 3:
                    entry.difficulties.hard++;
                    break;
                case 'Very Hard':
                case 'VeryHard':
                case '4':
                case 4:
                    entry.difficulties.veryHard++;
                    break;
                case 'Impossible':
                case '5':
                case 5:
                    entry.difficulties.impossible++;
                    break;
                default:
                    break;
            }
        });

        return Array.from(map.values()).sort((a, b) =>
            a.title.localeCompare(b.title)
        );
    }, [questions]);

    // =========================
    // SERIALIZER
    // =========================

    const serializeQuestionForUpdate = (q: Question) => ({
        questionId: q.questionId,
        text: q.text,
        questionType: q.questionType,
        mcOptions: q.mcOptions ?? null,
        correctAnswers: q.correctAnswers ?? [],
        hints: q.hints ?? [],
        explanation: q.explanation ?? '',
        difficulty: q.difficulty ?? '',
        chapter: q.chapter ?? '',
        feedback: q.feedback ?? '',
        isNew: q.isNew ?? false,
        needsReview: q.needsReview ?? false,
        timesAnswered: q.timesAnswered ?? 0,
        timesCorrect: q.timesCorrect ?? 0,
        isActive: q.isActive ?? true,
        authorUserId: q.authorUserId,
        lastEditedByUserId: q.lastEditedByUserId,
        version: q.version ?? 1,
        sourceId: q.sourceId,
    });

    // =========================
    // MUTATIONS
    // =========================

    const addQuestion = useCallback(async (newQuestion: any) => {
        await createMutation.mutateAsync(newQuestion);
    }, [createMutation]);

    const updateQuestion = useCallback(async (question: any) => {
        const payload = serializeQuestionForUpdate(question);
        await updateMutation.mutateAsync(payload);
    }, [updateMutation]);

    const deleteQuestion = useCallback(async (questionId: any) => {
        await deleteMutation.mutateAsync(questionId);
    }, [deleteMutation]);

    // =========================
    // CONTEXT VALUE
    // =========================

    const value = useMemo((): QuestionsContextValue => ({
        questions: questions as Question[],
        sources,
        activeQuestions,

        difficultyMap,
        difficulties,

        questionTypeMap,
        questionTypes,

        // stats for breakdown page
        difficultyStats,
        sourceStats,
        totalQuestions,

        isLoading,
        isError,
        error,

        addQuestion,
        updateQuestion,
        deleteQuestion,

        isCreating: createMutation.isPending,
        isUpdating: updateMutation.isPending,
        isDeleting: deleteMutation.isPending,
    }), [
        questions,
        sources,
        activeQuestions,

        difficultyStats,
        sourceStats,
        totalQuestions,

        difficultyMap,
        difficulties,
        questionTypeMap,
        questionTypes,

        isLoading,
        isError,
        error,

        addQuestion,
        updateQuestion,
        deleteQuestion,

        createMutation.isPending,
        updateMutation.isPending,
        deleteMutation.isPending,
    ]);

    return (
        <QuestionsContext.Provider value={value}>
            {children}
        </QuestionsContext.Provider>
    );
};

// =========================
// HOOKS
// =========================

export const useQuestionsContext = () => {
    const context = useContext(QuestionsContext);
    if (!context) {
        throw new Error(
            'useQuestionsContext must be used within a QuestionsProvider'
        );
    }
    return context;
};

export const useSourceHelpers = () => {
    const { sources } = useQuestionsContext();

    const uniqueSourceTypes = useMemo(() => {
        const types = Array.from(new Set(sources.map((s) => s.type)));
        return types.sort((a, b) => a.localeCompare(b));
    }, [sources]);

    const getSourcesByType = useCallback(
        (type: string) => {
            if (!type) return [];
            return sources.filter((s) => s.type === type);
        },
        [sources]
    );

    return { uniqueSourceTypes, getSourcesByType };
};